import ProductModel from 'coreClasses/product/ProductModel';
import {EEventSource, EEventType} from 'coreClasses/events/IEvents';
import VariantModel from 'coreClasses/product/VariantModel';

import ManagerRepository from './ManagerRepository';
import {debounceWithQueue} from '../utils/debounceWithQueue';

/**
 * This class is responsible for correct emission of view_item_list event
 * Because products information may load much later that the tiles are in viewport
 * we need a logic to control this.
 */
export class ViewItemListEmitter {
	private managerRepository: ManagerRepository;
	private productsInViewportIds = new Set<string>();
	private emittedProducts = new Set<string>();
	private readyProducts: Array<VariantModel | ProductModel> = [];
	private categoryId: string | null = null;
	private source: EEventSource | null = null;

	/**
	 * Represents ViewItemListEmitter
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		this.managerRepository = managerRepository;
	}

	/**
	 * Set category id for the event
	 * @param {string | null} categoryId - categoryId
	 */
	public setCategoryId(categoryId: string | null): void {
		this.categoryId = categoryId;
	}

	/**
	 * Get category id for the event
	 * @return {string | null} categoryId - categoryId
	 */
	public getCategoryId(): string | null {
		return this.categoryId;
	}

	/**
	 * Set source of the event
	 * @param {EEventSource | null} source - event source
	 */
	public setSource(source: EEventSource | null): void {
		this.source = source;
	}

	/**
	 * Check if products has all the necessary data to emit the event
	 * @param {Array<VariantModel | ProductModel>} products - list with products
	 */
	public setReadyProducts(products: Array<VariantModel | ProductModel>): void {
		if (!products || this.readyProducts.length === products.length) {
			// these products were emitted earlier
			return;
		}

		const currency = this.managerRepository.getLocalesMgr().getCurrentLocale().getCurrencyCode();

		const readyProducts = products.filter(
			(p) =>
				p.isReady() &&
				p.getName() &&
				p.getFullVariants().length > 0 &&
				p.getPriceModel().getMinPrice().currency === currency, // if user changed the currency
		);

		// if all products are ready, we can emit the event
		if (readyProducts.length > 0 && readyProducts.length === products.length) {
			this.readyProducts = readyProducts;

			this.emitEvent();
		}
	}

	/**
	 * If a product appeared in the viewport, emit event.
	 * This function is debounced because a few products may appear in viewport at the same time.
	 * So we can queue them together
	 * @param {string} productId - id of the product to add
	 */
	public setVisibleProduct = debounceWithQueue((visibleProductIds: string[]) => {
		this.productsInViewportIds = new Set(visibleProductIds);

		this.emitEvent();
	}, 100);

	/**
	 * Clear all data about products
	 */
	public clearAllProductsData(): void {
		this.emittedProducts = new Set<string>();
		this.productsInViewportIds = new Set<string>();
		this.readyProducts = [];
	}

	/**
	 * Emit view_item_list event
	 */
	private emitEvent(): void {
		const productsToEmit = this.readyProducts.filter(
			(p) => !this.emittedProducts.has(p.getProductId()) && this.productsInViewportIds.has(p.getProductId()),
		);

		if (!productsToEmit.length) {
			return;
		}

		const categoriesMgr = this.managerRepository.getCategoriesMgr();
		const currency = this.managerRepository.getLocalesMgr().getCurrentLocale().getCurrencyCode();
		let eventSourceCategory = null;

		if (this.getCategoryId()) {
			eventSourceCategory = categoriesMgr.getCategory(this.getCategoryId());
		}

		const productIndexOffset = this.readyProducts.indexOf(productsToEmit[0]);
		const productsData = productsToEmit.map((product, i) => {
			const productCategory = categoriesMgr?.getCategory(product.getPrimaryCategoryId());
			const productCategoryTree = productCategory?.getParentCategoryTree();

			this.emittedProducts.add(product.getProductId());

			return {
				product: product,
				productCategoriesIds: productCategoryTree?.map((cat) => cat.getId()),
				position: productIndexOffset + i,
				eventSourceCategoryId: productCategory?.getId(),
				eventSourceCategoryName: productCategory?.getName(),
			};
		});

		this.managerRepository.getEventsMgr().emit(EEventType.VIEW_ITEM_LIST, {
			productIndexOffset,
			eventSourceCategoryId: eventSourceCategory?.getId(),
			eventSourceCategoryName: eventSourceCategory?.getName(),
			currency,
			productsData,
			source: this.source,
		});
	}
}
